import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BusinessEventDetailDrawer } from "./business-events/BusinessEventDetailDrawer";
import { businessEventLabel, businessEventLabels, eventIcon, severityLabels } from "./business-events/businessEventLabels";
import type { BusinessEventDto, BusinessEventFilters, BusinessEventSeverity } from "./business-events/businessEventTypes";
import { useBusinessEventsLiveFeed } from "./business-events/useBusinessEventsLiveFeed";

const initialFilters: BusinessEventFilters = {
  dateFrom: "",
  dateTo: "",
  type: "",
  severity: "",
  zoneId: "",
  equipeId: "",
  clientId: "",
  tourneeId: "",
  actorId: "",
  q: "",
};

function severityClass(severity: BusinessEventSeverity): string {
  if (severity === "CRITICAL") return "border-rose-500/50 bg-rose-500/10 text-rose-200";
  if (severity === "WARNING") return "border-amber-500/50 bg-amber-500/10 text-amber-200";
  return "border-sky-500/40 bg-sky-500/10 text-sky-200";
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }).format(new Date(value));
}

function quickLinks(event: BusinessEventDto): React.JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      {event.client && event.links.client ? <Link to={event.links.client} className="rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-sky-200 hover:bg-slate-800">Voir client</Link> : null}
      {event.passage && event.links.passage ? <Link to={event.links.passage} className="rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-sky-200 hover:bg-slate-800">Voir passage</Link> : null}
      {event.links.facture ? (
        <Link to={event.links.facture} className="rounded-lg border border-slate-700 px-2.5 py-1 text-xs text-sky-200 hover:bg-slate-800">
          Voir factures
        </Link>
      ) : null}
    </div>
  );
}

export default function AdminBusinessEventsPage(): React.JSX.Element {
  const [filters, setFilters] = useState<BusinessEventFilters>(initialFilters);
  const [selected, setSelected] = useState<BusinessEventDto | null>(null);
  const { items, stats, loading, loadingMore, error, hasMore, load, loadMore } = useBusinessEventsLiveFeed(filters);
  const hasActiveFilters = useMemo(() => Object.values(filters).some((value) => value.trim()), [filters]);
  const typeOptions = useMemo(() => Object.entries(businessEventLabels), []);

  function updateFilter(key: keyof BusinessEventFilters, value: string): void {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function setToday(): void {
    const today = new Date().toISOString().slice(0, 10);
    setFilters((current) => ({ ...current, dateFrom: today, dateTo: today }));
  }

  return (
    <div className="space-y-6 text-slate-100">
      <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-300">Audit terrain</p>
            <h1 className="mt-2 text-2xl font-bold text-white">Journal opérationnel</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Suivez les affectations, tournées, validations, blocages et événements terrain en temps réel.
            </p>
          </div>
          <button type="button" onClick={() => void load()} disabled={loading} className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-700 disabled:opacity-50">
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {[
          ["Événements aujourd’hui", stats.today],
          ["Alertes critiques", stats.critical],
          ["Blocages actifs", stats.blocked],
          ["Réaffectations", stats.reassignments],
          ["Validations terrain", stats.validations],
          ["GPS suspects", stats.gpsSuspects],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-semibold text-white">{value}</p>
          </div>
        ))}
      </div>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} />
          <select className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" value={filters.type} onChange={(e) => updateFilter("type", e.target.value)}>
            <option value="">Tous les événements</option>
            {typeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" value={filters.severity} onChange={(e) => updateFilter("severity", e.target.value)}>
            <option value="">Toutes criticités</option>
            <option value="INFO">{severityLabels.INFO}</option>
            <option value="WARNING">{severityLabels.WARNING}</option>
            <option value="CRITICAL">{severityLabels.CRITICAL}</option>
          </select>
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Recherche texte" value={filters.q} onChange={(e) => updateFilter("q", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Zone ID" value={filters.zoneId} onChange={(e) => updateFilter("zoneId", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Équipe ID" value={filters.equipeId} onChange={(e) => updateFilter("equipeId", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Client ID" value={filters.clientId} onChange={(e) => updateFilter("clientId", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Tournée ID" value={filters.tourneeId} onChange={(e) => updateFilter("tourneeId", e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Acteur ID" value={filters.actorId} onChange={(e) => updateFilter("actorId", e.target.value)} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => void load()} className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500">Actualiser</button>
          <button type="button" onClick={setToday} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">Voir aujourd’hui</button>
          <button type="button" onClick={() => setFilters(initialFilters)} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">Réinitialiser les filtres</button>
        </div>
      </section>

      {error ? <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p> : null}

      <section className="rounded-2xl border border-slate-800 bg-slate-900/50">
        <div className="border-b border-slate-800 px-5 py-4">
          <h2 className="font-semibold text-white">Timeline opérationnelle</h2>
        </div>
        <div className="divide-y divide-slate-800">
          {items.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <p className="font-medium text-slate-200">Aucune activité opérationnelle sur cette période.</p>
              <p className="mt-2 text-sm text-slate-500">Les affectations, tournées, validations et blocages apparaîtront ici automatiquement.</p>
              {!hasActiveFilters ? (
                <div className="mt-4 flex justify-center gap-2">
                  <button type="button" onClick={() => void load()} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">Actualiser</button>
                  <button type="button" onClick={setToday} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">Voir aujourd’hui</button>
                </div>
              ) : null}
            </div>
          ) : null}
          {items.map((event) => (
            <article key={event.id} className="grid gap-4 p-5 hover:bg-slate-800/30 md:grid-cols-[90px_1fr_auto]">
              <div className="text-sm text-slate-400">{formatTime(event.createdAt)}</div>
              <div className="flex gap-4">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${severityClass(event.severity)}`}>
                  {eventIcon(event.type)}
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-white">{event.title || businessEventLabel(event.type)}</h3>
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${severityClass(event.severity)}`}>{severityLabels[event.severity]}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-400">{event.message ?? "Action enregistrée dans le journal opérationnel."}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    Acteur : {event.actor?.name ?? "Action système"}
                    {event.equipe?.name ? ` · Équipe : ${event.equipe.name}` : ""}
                    {event.client?.name ? ` · Client : ${event.client.name}` : ""}
                  </p>
                  <div className="mt-3">{quickLinks(event)}</div>
                </div>
              </div>
              <button type="button" onClick={() => setSelected(event)} className="h-fit rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">
                Détail
              </button>
            </article>
          ))}
        </div>
        {hasMore ? (
          <div className="border-t border-slate-800 p-4 text-center">
            <button type="button" disabled={loadingMore} onClick={() => void loadMore()} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50">
              {loadingMore ? "Chargement..." : "Charger plus"}
            </button>
          </div>
        ) : null}
      </section>

      <BusinessEventDetailDrawer event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
